import { h as __h } from "preact";
import { Component } from "preact";
import html from "~/util/html.js";
const tick = {
  /** @type {Set<Time>} */
  live: new Set(),
  /** @type {ReturnType<typeof setInterval> | undefined} */
  interval: undefined,
  /** @param {Time} time */
  ref(time) {
    this.live.add(time);
    this.interval ??= setInterval(() => {
      for (const t of this.live) t.forceUpdate();
    }, 1000);
    return () => {
      this.live.delete(time);
      if (this.live.size === 0) {
        clearInterval(this.interval);
        this.interval = undefined;
      }
    };
  }
};

/**
 * @extends {Component<ActionsProps>}
 *
 * @typedef {object} ActionsProps
 * @property {Date} datetime
 * @property {Date} [now]
 * @property {{ format: (date: Date, now?: Date) => string }} [fmt]
 * @property {boolean} [tick]
 */
export default class Time extends Component {
  /** @type {(() => void) | undefined} */
  untick;

  /** @override */
  componentDidMount() {
    if (this.props.tick) this.untick = tick.ref(this);
  }

  /** @override */
  componentWillUnmount() {
    this.untick?.();
  }
  render() {
    if (Number.isNaN(this.props.datetime.getTime())) return "—";
    const fmt = this.props.fmt ?? mdy;
    return __h("time", {
      datetime: this.props.datetime.toISOString()
    }, fmt.format(this.props.datetime, this.props.now));
  }
}

/** A date, e.g. "June 23, 2026". */
export const mdy = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric"
});

/**
 * A date and time, skipping parts that match now, e.g. "3:32 PM", "June 23 at 3:32 PM" or
 * "June 23, 2026 at 3:32 PM".
 */
export const timestamp = {
  time: new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "numeric"
  }),
  sameYear: new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
  }),
  full: new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
  }),
  /**
   * @param {Date} date
   * @param {Date} [now]
   */
  format(date, now) {
    const dateNow = now ?? new Date();
    if (date.getFullYear() !== dateNow.getFullYear()) return this.full.format(date);
    if (date.getMonth() !== dateNow.getMonth() || date.getDate() !== dateNow.getDate()) {
      return this.sameYear.format(date);
    }
    return this.time.format(date);
  }
};

/**
 * A time and date, skipping parts that match now, e.g. "3:32 PM", "3:32 PM on Jun. 23" or
 * "3:32 PM on Jun. 23, 2025".
 */
export const timeOnDate = {
  time: new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "numeric"
  }),
  month: new Intl.DateTimeFormat("en-US", {
    month: "short"
  }),
  /**
   * @param {Date} date
   * @param {Date} [now]
   */
  format(date, now) {
    const dateNow = now ?? new Date();
    const time = this.time.format(date);
    const sameYear = date.getFullYear() === dateNow.getFullYear();
    if (sameYear && date.getMonth() === dateNow.getMonth() && date.getDate() === dateNow.getDate()) {
      return time;
    }
    const month = this.month.format(date);
    const day = `${month === "May" ? month : `${month}.`} ${date.getDate()}`;
    return `${time} on ${sameYear ? day : `${day}, ${date.getFullYear()}`}`;
  }
};

/** Time relative to now in words, e.g. "2 hours ago". */
export const relative = {
  rtf: new Intl.RelativeTimeFormat("en", {
    numeric: "auto",
    style: "short"
  }),
  /**
   * @param {Date} date
   * @param {Date} [now]
   */
  format(date, now) {
    const dateNow = now ?? new Date();
    const sec = Math.round((date.getTime() - dateNow.getTime()) / 1000);
    if (Math.abs(sec) < 60) return this.rtf.format(sec, "second");
    const mins = Math.round(sec / 60);
    if (Math.abs(mins) < 60) return this.rtf.format(mins, "minute");
    const hrs = Math.round(mins / 60);
    if (Math.abs(hrs) < 24) return this.rtf.format(hrs, "hour");
    return this.rtf.format(Math.round(hrs / 24), "day");
  }
};

/** Elapsed time since now as an h:m:s duration, e.g. "1:05:33". */
export const duration = {
  digital: new Intl.DurationFormat("en", {
    style: "digital",
    hoursDisplay: "always"
  }),
  /**
   * @param {Date} date
   * @param {Date} [now]
   */
  format(date, now) {
    const dateNow = now ?? new Date();
    const total = Math.max(0, Math.floor((dateNow.getTime() - date.getTime()) / 1000));
    return this.digital.format({
      hours: Math.floor(total / 3600),
      minutes: Math.floor(total / 60) % 60,
      seconds: total % 60
    });
  }
};